// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import FloatingTooltip, { FloatingTooltipStyles } from "@eisinc/App/Components/FloatingTooltip/FloatingTooltip";
import HeaderClickable, { HeaderClickableStyles } from "@eisinc/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@eisinc/App/Components/MiniMyAccount/MiniMyAccount";
import XngSignIn from "@eisinc/App/Components/XngSignIn/XngSignIn";
import getSystemList from "@eisinc/App/Handlers/XNG/getSystemList";
import { GetSystemListValuesModel } from "@eisinc/App/Services/SystemListService";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { getIsCheckoutPage } from "@eisinc/App/Store/Selectors/CheckoutSelector";
import { xIsAuthenticated } from "@eisinc/App/Store/Selectors/GeneralSelectors";
import { getAllowedLinks } from "@eisinc/App/Store/Selectors/HeaderSelector";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import SignIn from "@eisinc/XDL/Icons/SignIn";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { MappedLink } from "@insite/mobius/Menu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    // XNG-Changes
    links = "links",
}

// XNG-Changes
export interface HeaderMyAccountLinkModel {
    fields: {
        isGroup: boolean;
        destination: LinkFieldValue;
        title: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        // XNG-Changes
        [fields.links]: HeaderMyAccountLinkModel[];
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const session = getSession(state);
    const billToState = getBillToState(state, state.context.session.billToId);

    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: session.firstName || session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
        onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
        // XNG-Changes
        isSticky: state.xHeader.isSticky,
        billToState,
        isAuthenticated: xIsAuthenticated(state),
        selectedBillToId: session.billToId,
        links: getAllowedLinks(state, ownProps.fields.links),
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
        sessionCurrencyCode: session.currency?.currencyCode,
        salesOrg: billToState?.value?.properties?.salesOrg,
        bTCurrencyCode: billToState?.value?.properties?.currencyCode,
        isTabletScreen: state.xCommon.isTabletScreen,
        isHorizontal: getIsCheckoutPage(state),
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Changes
    loadBillTo,
    getSystemList,
    setFlyoutDrawerIsOpen,
    setNavDrawerIsOpen,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ButtonPresentationProps;
    // XNG-Changes
    myAccountHeaderClickable?: HeaderClickableStyles;
    signInHeaderClickable?: HeaderClickableStyles;
    tooltip?: FloatingTooltipStyles;
    myAccountTooltip?: FloatingTooltipStyles;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    // XNG-Changes
    isTabletScreen,
    isHorizontal,
    links,
    isAuthenticated,
    selectedBillToId,
    billToState,
    loadBillTo,
    getSystemList,
    sessionCurrencyCode,
    salesOrg,
    bTCurrencyCode,
    setFlyoutDrawerIsOpen,
    setNavDrawerIsOpen,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    // XNG-Changes
    const [allowedCurrencies, setAllowedCurrencies] = React.useState<GetSystemListValuesModel[]>();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);
    const toasterContext = React.useContext(ToasterContext);

    const isAllowedToPaymentCenter = React.useMemo(() => {
        const currencyCode = bTCurrencyCode || sessionCurrencyCode;
        const role = `${salesOrg}_${currencyCode}`;

        return allowedCurrencies?.some(currency => [role].includes(currency.name));
    }, [salesOrg, bTCurrencyCode, sessionCurrencyCode, allowedCurrencies]);

    const loadAllowedCurrencies = () => {
        getSystemList({
            systemListName: "Custom_AllowedInvoicePaymentCustomers",
            onError: (errorMessage: string) => {
                toasterContext.addToast({ body: errorMessage, messageType: "danger" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    setAllowedCurrencies(resultProps.apiResult.result);
                }
            },
        });
    };

    React.useEffect(() => {
        // load current billTo
        if (!billToState.value && !billToState.isLoading && selectedBillToId && isAuthenticated) {
            loadBillTo({ billToId: selectedBillToId });
        }
        loadAllowedCurrencies();
    }, []);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        // XNG-Changes: new icon
        icon = SignIn;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    /**
     * TODO: XNG-Changes
     * We need it to return url. later should back to ootb code
     * @param e
     */

    const onSignInHandler = (e: React.MouseEvent) => {
        e.preventDefault();

        // setTimeout(() => {
        //     // XNG-CHANGES : To make sure all tippy are hidden and scrollbar is present
        //     removeScrollbarGutter();
        //     ariaAppHider.show(document.body);
        // }, 500);

        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
            return;
        }
        if (isAuthenticated) {
            history.push(myAccountPageLink?.url || "/MyAccount");
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In / Register");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const headerClickableComponent = (
        <HeaderClickable
            isOpen={isMenuOpen}
            onClick={onSignInHandler}
            data-test-selector="header_signIn"
            isHorizontal={isHorizontal}
            extendedStyles={isAuthenticated ? styles.myAccountHeaderClickable : styles.signInHeaderClickable}
        >
            {isAuthenticated ? translate("My Account") : translate("Sign In / Register")}{" "}
        </HeaderClickable>
    );

    // XNG-Change
    const closeNavigationDrawer = () => {
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    if (isTabletScreen) {
        return headerClickableComponent;
    }

    return (
        <StyledWrapper {...styles.signOutWrapper}>
            <FloatingTooltip
                trigger="hover"
                placement="bottom-end"
                offset={12}
                overlay={true}
                delay={{ open: 100, close: 350 }}
                onOpenChange={open => setIsMenuOpen(open)}
                triggerComponent={headerClickableComponent}
                extendedStyles={isAuthenticated ? styles.myAccountTooltip : styles.tooltip}
                disableFlip={true}
            >
                {isAuthenticated ? (
                    <MiniMyAccount
                        isPunchOutSession={isPunchOutSession}
                        links={links}
                        isAllowedToPaymentCenter={isAllowedToPaymentCenter}
                        onSignOutHandler={onSignOutHandler}
                    />
                ) : (
                    isMenuOpen && <XngSignIn />
                )}
            </FloatingTooltip>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In / MyAccount Links",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                isVisible: item => item.fields.includeAccountMenu,
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;

                    if (item.fields.isGroup) {
                        return `Group Name: ${item.fields.title}`;
                    }

                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }

                    if (type === "Category") {
                        return value;
                    }
                    if (type === "Url") {
                        return value;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 4,
                fieldDefinitions: [
                    {
                        name: "isGroup",
                        displayName: "Is group",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "destination",
                        editorTemplate: "CustomLinkField" as any,
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isVisible: item => !item.fields.isGroup,
                    },
                    {
                        name: "title",
                        displayName: "Groupe Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isVisible: item => item.fields.isGroup === true,
                    },
                    {
                        name: fields.showPricingToggle,
                        displayName: "Show/Hide Pricing Toggle",
                        editorTemplate: "CheckboxField",
                        isVisible: widget => widget.fields.includeAccountMenu,
                        defaultValue: false,
                        sortOrder: 4,
                    },
                    {
                        name: fields.showInventoryToggle,
                        displayName: "Show/Hide Inventory Toggle",
                        editorTemplate: "CheckboxField",
                        isVisible: widget => widget.fields.includeAccountMenu,
                        defaultValue: false,
                        sortOrder: 5,
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
